import {
  type Control,
  Controller,
  type UseFormRegister,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form'

import { Banner } from '@/components/ui/banner'
import { YesNo } from '@/components/ui/button-group'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Equipment } from '../../../../models/equipment'
import { equipmentOption, OWNERSHIP_OPTIONS, PrefilledField } from './ClaimStep2Fields'
import { NOT_LISTED, type Step2FormValues } from './claimStep2Values'

const TRAILER_NOT_LISTED = { value: NOT_LISTED, label: 'Trailer not on this list' }

interface ClaimTrailerSectionProps {
  control: Control<Step2FormValues>
  register: UseFormRegister<Step2FormValues>
  setValue: UseFormSetValue<Step2FormValues>
  trailers: Equipment[]
}

export function ClaimTrailerSection({ control, register, setValue, trailers }: ClaimTrailerSectionProps) {
  const trailerAttached = useWatch({ control, name: 'trailerAttached' })
  const trailerSelection = useWatch({ control, name: 'trailerSelection' })
  const trailerManual = trailerSelection === NOT_LISTED
  const selectedTrailer = trailers.find((t) => t.id === trailerSelection)

  /** The picked unit's details, or empty boxes for a trailer that is not on the list. */
  function seed(trailer: Equipment | undefined) {
    setValue('trailerVin', trailer?.vin ?? '')
    setValue('trailerPlate', trailer?.licensePlate ?? '')
    setValue('trailerYearMakeModel', '')
    setValue('trailerOwnership', '')
  }

  return (
    <>
      <div className="border-t pt-4">
        <Controller
          name="trailerAttached"
          control={control}
          rules={{ validate: (val) => val !== null || 'Please choose.' }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">Was a trailer attached? *</label>
              <YesNo value={field.value} onChange={field.onChange} />
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>

      {trailerAttached === true && (
        <>
          <Controller
            name="trailerSelection"
            control={control}
            rules={{
              validate: (val, f) => f.trailerAttached !== true || !!val || 'Select the trailer.',
            }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-neutral-700" htmlFor="claim-trailer">
                  Which trailer was attached? *
                </label>
                <SearchableSelect
                  id="claim-trailer"
                  options={trailers.map(equipmentOption)}
                  value={field.value || undefined}
                  onChange={(next) => {
                    // On the pick itself, so the fleet list arriving never overwrites a saved claim.
                    if (next !== field.value) seed(trailers.find((t) => t.id === next))
                    field.onChange(next)
                  }}
                  footerOption={TRAILER_NOT_LISTED}
                  placeholder="— Select trailer —"
                  searchPlaceholder="Search unit number, VIN or plate"
                  emptyMessage="No trailer matches that"
                  aria-invalid={fieldState.error ? true : undefined}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />

          {trailerManual && (
            <>
              <Banner variant="warning" small>
                This trailer is not on your policy. Please enter details manually. Your broker will review
                coverage implications before submitting to the carrier.
              </Banner>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-neutral-700">Trailer license plate</label>
                  <Input placeholder="Leave blank if you don't have it" {...register('trailerPlate')} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-neutral-700">Trailer unit / VIN</label>
                  <Input placeholder="Unit number or VIN if available" {...register('trailerVin')} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-neutral-700">Year / Make / Model</label>
                  <Input placeholder="e.g. 2021 Great Dane Dry Van" {...register('trailerYearMakeModel')} />
                </div>
                <Controller
                  name="trailerOwnership"
                  control={control}
                  rules={{
                    validate: (val, f) =>
                      f.trailerAttached !== true ||
                      f.trailerSelection !== NOT_LISTED ||
                      !!val ||
                      'Please pick ownership.',
                  }}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-neutral-700">Trailer ownership *</label>
                      <Select value={field.value || undefined} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full" aria-invalid={fieldState.error ? true : undefined}>
                          <SelectValue placeholder="— Select —" />
                        </SelectTrigger>
                        <SelectContent>
                          {OWNERSHIP_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FieldError message={fieldState.error?.message} />
                    </div>
                  )}
                />
              </div>
            </>
          )}

          {selectedTrailer && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <PrefilledField label="VIN" {...register('trailerVin')} />
              <PrefilledField
                label="License plate"
                placeholder="Add the plate if we do not have it"
                {...register('trailerPlate')}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}
